import { useMemo, useRef, useState } from 'react'
import type { EstoqueRow } from '../../hooks/useEstoqueAnalytics'
import { useCompras } from '../../hooks/useCompras'
import KpiCard from './KpiCard'
import BarChart from './charts/BarChart'
import Alert from '../ui/Alert'
import { fmtBRL, fmtDays, fmtNum, fmtPct } from '../../lib/format'
import { isRupturaAcionavel } from '../../lib/replenishment'
import { LEAD_TIME_TARGET, leadTimeFulfillmentIndex } from '../../lib/leadTime'
import { kpiMeta } from '../../lib/kpiMeta'
import { useInstallments } from '../../hooks/useInstallments'
import MetasComprasSection from './MetasComprasSection'
import OrcamentoMensalCard from './OrcamentoMensalCard'
import SafetyStockCard from './SafetyStockCard'

interface Props {
  rows: EstoqueRow[]
  /** Índice geral de encalhe (fração) calculado pelo dashboard — alimenta a meta de encalhe. */
  encalheIndex: number | null
  monthNumber: number
  monthLabel: string
  monthKey: string
  todayKey: string
  range?: { start: string; end: string }
}

export default function ComprasPanel({ rows, encalheIndex, monthNumber, monthLabel, monthKey, todayKey, range }: Props) {
  const { orders, poItems, loading, error } = useCompras()
  const { sumNext } = useInstallments()

  const stats = useMemo(() => {
    const now = Date.now()

    // POs abertas (Pendente + Enviado)
    const openPoIds = new Set(orders.filter(o => o.status === 'Pendente' || o.status === 'Enviado').map(o => o.id))
    const openPoProductIds = new Set(poItems.filter(i => openPoIds.has(i.purchase_order_id)).map(i => i.product_id))

    // Lead time real por PO recebida
    const received = orders.filter(o => o.status === 'Recebido' && o.sent_at && o.received_at)

    // Agrupa lead times reais por fornecedor
    const supplierLeadMap = new Map<string, { name: string; expected: number; actuals: number[] }>()
    for (const po of received) {
      if (!po.sent_at || !po.received_at || !po.supplier_id) continue
      const realDays = (new Date(po.received_at).getTime() - new Date(po.sent_at).getTime()) / 86_400_000
      if (!supplierLeadMap.has(po.supplier_id)) {
        supplierLeadMap.set(po.supplier_id, {
          name: po.supplier_name ?? po.supplier_id,
          expected: po.lead_time_days ?? 0,
          actuals: [],
        })
      }
      supplierLeadMap.get(po.supplier_id)!.actuals.push(realDays)
    }

    const supplierLeadSummary = Array.from(supplierLeadMap.values())
      .map(s => ({
        name: s.name,
        expected: s.expected,
        actual: s.actuals.reduce((a, b) => a + b, 0) / s.actuals.length,
        count: s.actuals.length,
      }))
      .sort((a, b) => (b.actual - b.expected) - (a.actual - a.expected))

    const avgLeadReal = received.length > 0
      ? received.reduce((sum, po) => {
          if (!po.sent_at || !po.received_at) return sum
          return sum + (new Date(po.received_at).getTime() - new Date(po.sent_at).getTime()) / 86_400_000
        }, 0) / received.length
      : null

    // POs em atraso (Enviado, past estimated_arrival ou sent_at + lead_time_days)
    const enviados = orders.filter(o => o.status === 'Enviado')
    const atrasadas = enviados.filter(po => {
      if (po.estimated_arrival) return new Date(po.estimated_arrival).getTime() < now
      if (po.sent_at && po.lead_time_days) {
        return new Date(po.sent_at).getTime() + po.lead_time_days * 86_400_000 < now
      }
      return false
    })

    // Valor total em trânsito
    const totalEmTransito = enviados.reduce((s, po) => s + (po.total_value ?? 0), 0)

    // Ruptura sem PO aberta (régua única — ruptura acionável, ao vivo)
    const rupturaSemPo = rows.filter(r => isRupturaAcionavel(r.current_stock, r.kanban_status) && !openPoProductIds.has(r.id))

    // Ruptura com PO aberta
    const rupturaComPo = rows.filter(r => isRupturaAcionavel(r.current_stock, r.kanban_status) && openPoProductIds.has(r.id))

    const abertas = orders.filter(o => o.status === 'Pendente' || o.status === 'Enviado')

    // Índice de atendimento ao lead time: % de pedidos recebidos na data prevista (meta 95%).
    const leadFulfillment = leadTimeFulfillmentIndex(orders, now)

    return {
      openCount: openPoIds.size,
      abertas,
      enviados,
      atrasadas,
      totalEmTransito,
      avgLeadReal,
      supplierLeadSummary,
      rupturaSemPo,
      rupturaComPo,
      leadFulfillment,
    }
  }, [orders, poItems, rows])

  // Card ativo dirige a tabela de POs (default: em atraso).
  const [activeCard, setActiveCard] = useState<'abertas' | 'atraso' | 'transito' | null>(null)
  const poTableRef = useRef<HTMLDivElement>(null)
  function selectCard(key: 'abertas' | 'atraso' | 'transito') {
    const next = activeCard === key ? null : key
    setActiveCard(next)
    if (next) poTableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
  const poList = useMemo(() => {
    if (activeCard === 'abertas') return { title: 'POs abertas (Pendente + Enviado)', rows: stats.abertas }
    if (activeCard === 'transito') return { title: 'Valor em trânsito — POs enviadas (maior primeiro)', rows: [...stats.enviados].sort((a, b) => (b.total_value ?? 0) - (a.total_value ?? 0)) }
    return { title: 'Compras urgentes — em atraso', rows: stats.atrasadas }
  }, [activeCard, stats])

  if (loading) {
    return <div className="flex items-center justify-center py-20"><div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" /></div>
  }
  if (error) return <Alert type="error" message={error} />

  // Metas dos KPIs (régua única → tom do card + selo). amarelo=2 preserva o "<3 atenção, ≥3 crítico".
  const atrasoMeta = kpiMeta({ value: stats.atrasadas.length, sentido: 'max', verde: 0, amarelo: 2, format: v => `${v}` })
  const leadMeta = kpiMeta({
    value: stats.leadFulfillment.index != null ? stats.leadFulfillment.index * 100 : null,
    sentido: 'min', verde: LEAD_TIME_TARGET * 100, amarelo: LEAD_TIME_TARGET * 100, format: v => `${v}%`,
  })

  return (
    <div className="flex flex-col gap-6">
      {/* ── Cap mensal de compras (Sprint 1) ───────────────────── */}
      <OrcamentoMensalCard orders={orders} monthKey={monthKey} />

      {/* ── Estoque de segurança por curva ABC (anti-ruptura, Fase 1) ── */}
      <SafetyStockCard />

      {/* ── Metas do mês (6 indicadores da Equipe de Compras) ──── */}
      <MetasComprasSection
        rows={rows}
        orders={orders}
        encalheIndex={encalheIndex}
        monthNumber={monthNumber}
        monthLabel={monthLabel}
        monthKey={monthKey}
        todayKey={todayKey}
        range={range}
      />

      {/* ── Cash flow forecast (Sprint 2 Ev.4) ─────────────────── */}
      {/* Cards de caixa (totais em R$ a pagar, sem meta) → sem selo. */}
      <div className="flex flex-wrap gap-2 [&>*]:w-[220px]">
        <KpiCard
          label="A pagar 30 dias"
          value={fmtBRL(sumNext(30))}
          tone="warning"
          hint="parcelas vencendo no mês corrente"
          analysis="Soma das parcelas com vencimento nos próximos 30 dias. Use para projetar caixa imediato e adiantar negociação se ficar pesado."
        />
        <KpiCard
          label="A pagar 60 dias"
          value={fmtBRL(sumNext(60))}
          tone="info"
          hint="rolante a partir de hoje"
          analysis="Soma rolante 0–60 dias. Compras com prazo 30/60 já contam aqui."
        />
        <KpiCard
          label="A pagar 90 dias"
          value={fmtBRL(sumNext(90))}
          tone="default"
          hint="rolante a partir de hoje"
          analysis="Soma rolante 0–90 dias. Útil para fechar mês com folga e negociar prazos."
        />
      </div>

      {/* ── KPIs ──────────────────────────────────────────────── */}
      <div className="flex flex-wrap gap-2 [&>*]:w-[220px]">
        <KpiCard
          label="POs abertas"
          value={fmtNum(stats.openCount)}
          tone="info"
          hint="clique para listar"
          onClick={() => selectCard('abertas')}
          active={activeCard === 'abertas'}
          analysis="Pedidos Pendente + Enviado. Acompanhe para não faltar nem sobrar."
        />
        <KpiCard
          label="Em atraso"
          value={stats.atrasadas.length}
          tone={atrasoMeta.tone}
          meta={{ status: atrasoMeta.status, label: atrasoMeta.metaLabel }}
          hint="clique para listar"
          onClick={() => selectCard('atraso')}
          active={activeCard === 'atraso'}
          analysis="POs enviadas além do prazo esperado. Ideal 0 — cobre o fornecedor."
        />
        <KpiCard
          label="Valor em trânsito"
          value={fmtBRL(stats.totalEmTransito)}
          tone="info"
          hint="clique para listar"
          onClick={() => selectCard('transito')}
          active={activeCard === 'transito'}
          analysis="R$ em POs enviadas a receber. Use para planejar caixa e recebimento."
        />
        <KpiCard
          label="Lead time médio real"
          value={stats.avgLeadReal != null ? fmtDays(stats.avgLeadReal) : '—'}
          tone="default"
          hint="média de sent_at → received_at"
          analysis="Dias médios entre envio e recebimento. Menor e estável = fornecedor confiável."
        />
        <KpiCard
          label="Atendimento lead time"
          value={stats.leadFulfillment.index != null ? fmtPct(stats.leadFulfillment.index * 100, 0) : '—'}
          tone={leadMeta.tone}
          meta={{ status: leadMeta.status, label: leadMeta.metaLabel }}
          hint={
            stats.leadFulfillment.evaluated > 0
              ? `${stats.leadFulfillment.onTime}/${stats.leadFulfillment.evaluated} na data · meta ${fmtPct(LEAD_TIME_TARGET * 100, 0)}`
              : `meta ${fmtPct(LEAD_TIME_TARGET * 100, 0)}`
          }
          analysis="% de pedidos recebidos entregues na data prevista. Meta: 95% dos pedidos entregues na data."
        />
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        {/* Lead time por fornecedor */}
        <section className="bg-surface rounded-xl border border-line p-4 flex flex-col gap-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-faint">Lead time real vs esperado</h3>
          {stats.supplierLeadSummary.length === 0 ? (
            <p className="text-sm text-ink-faint">Sem POs recebidas para calcular.</p>
          ) : (
            <>
              <BarChart
                horizontal
                bars={stats.supplierLeadSummary.slice(0, 6).map(s => ({
                  label: s.name.length > 14 ? s.name.slice(0, 13) + '…' : s.name,
                  value: s.actual,
                  color: s.actual > s.expected * 1.1 ? '#ef4444' : s.actual > s.expected ? '#f97316' : '#34d399',
                }))}
                formatValue={v => fmtDays(v)}
              />
              <p className="text-[11px] text-ink-faint">Dias reais · vermelho = atraso significativo vs lead time esperado</p>
            </>
          )}
        </section>

        {/* Tabela de POs dirigida pelo card ativo (default: em atraso) */}
        <section ref={poTableRef} className="bg-surface rounded-xl border border-line p-4 flex flex-col gap-3">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-faint">{poList.title}</h3>
            {activeCard && (
              <button
                onClick={() => setActiveCard(null)}
                className="text-[11px] px-2 py-0.5 rounded bg-sunken hover:bg-line text-ink-muted border border-line"
              >
                Limpar
              </button>
            )}
          </div>
          {poList.rows.length === 0 ? (
            <p className="text-sm text-emerald-400">Nenhuma PO nesta lista.</p>
          ) : (
            <div className="overflow-y-auto max-h-[420px]">
              <table className="w-full text-sm mobile-stack-table">
                <thead className="sticky top-0 bg-surface">
                  <tr className="text-left text-[11px] uppercase tracking-wide text-ink-faint border-b border-line">
                    <th className="pb-2 font-medium">Fornecedor</th>
                    <th className="pb-2 font-medium text-right">Enviado</th>
                    <th className="pb-2 font-medium text-right">Previsão</th>
                    <th className="pb-2 font-medium text-right">Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {poList.rows.map(po => (
                    <tr key={po.id} className="border-b border-line/50 last:border-0">
                      <td className="py-1.5 pr-3 text-ink max-w-[160px] truncate">{po.supplier_name ?? '—'}</td>
                      <td className="py-1.5 pr-3 text-right text-ink-muted text-xs">
                        {po.sent_at ? new Date(po.sent_at).toLocaleDateString('pt-BR') : '—'}
                      </td>
                      <td className="py-1.5 pr-3 text-right text-red-400 text-xs">
                        {po.estimated_arrival
                          ? new Date(po.estimated_arrival).toLocaleDateString('pt-BR')
                          : po.lead_time_days && po.sent_at
                            ? new Date(new Date(po.sent_at).getTime() + po.lead_time_days * 86_400_000).toLocaleDateString('pt-BR')
                            : '—'}
                      </td>
                      <td className="py-1.5 text-right text-ink-muted">{po.total_value != null ? fmtBRL(po.total_value) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      {/* Ruptura sem cobertura */}
      <section className="bg-surface rounded-xl border border-line p-4 flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-faint">Ruptura sem PO aberta</h3>
          <div className="flex gap-2 text-xs text-ink-faint">
            <span className="text-emerald-400 font-medium">{stats.rupturaComPo.length}</span> com PO ·
            <span className="text-red-400 font-medium">{stats.rupturaSemPo.length}</span> sem cobertura
          </div>
        </div>
        {stats.rupturaSemPo.length === 0 ? (
          <p className="text-sm text-emerald-400">Todos os itens em ruptura têm PO aberta.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm mobile-stack-table">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-ink-faint border-b border-line">
                  <th className="pb-2 font-medium">SKU</th>
                  <th className="pb-2 font-medium">Produto</th>
                  <th className="pb-2 font-medium text-center">Curva</th>
                  <th className="pb-2 font-medium text-right">Saldo</th>
                  <th className="pb-2 font-medium text-right">Cobertura</th>
                </tr>
              </thead>
              <tbody>
                {stats.rupturaSemPo
                  .sort((a, b) => (b.abc_curve === 'A' ? 1 : 0) - (a.abc_curve === 'A' ? 1 : 0))
                  .slice(0, 15)
                  .map(r => (
                    <tr key={r.id} className="border-b border-line/50 last:border-0">
                      <td className="py-1.5 pr-4 text-ink-muted font-mono text-xs">{r.sku}</td>
                      <td className="py-1.5 pr-4 text-ink max-w-[220px] truncate">{r.name}</td>
                      <td className="py-1.5 pr-4 text-center">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                          r.abc_curve === 'A' ? 'bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300' :
                          r.abc_curve === 'B' ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300' :
                          'bg-sunken text-ink-muted'
                        }`}>{r.abc_curve ?? '—'}</span>
                      </td>
                      <td className="py-1.5 pr-4 text-right text-ink-muted">{fmtNum(r.current_stock)}</td>
                      <td className="py-1.5 text-right text-red-400">
                        {r.coverage_days != null ? fmtDays(r.coverage_days) : 'zerado'}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
            {stats.rupturaSemPo.length > 15 && (
              <p className="text-[11px] text-ink-faint mt-2">+{stats.rupturaSemPo.length - 15} itens adicionais</p>
            )}
          </div>
        )}
      </section>
    </div>
  )
}
